import {
  createSlice,
  createAsyncThunk,
  createSelector } from
"@reduxjs/toolkit";
import { ApolloClient, gql, NormalizedCacheObject } from "@apollo/client";
import { RootState } from "@src/redux/store";
import * as TYPES from "@src/redux/types";

const GRAPHQL = gql`
  query getKitList(
    $filter: kitFilterInput
    $pagination: paginationInput
    $sort: [sortInput]
  ) {
    getKitList(filter: $filter, pagination: $pagination, sort: $sort) {
      items {
        organization {
          id
          name
          level {
            id
            name
            allowOperatorLogin
            allowKitAssign
          }
        }
        operator {
          id
          name
        }
        subscriptionPlan {
          id
          name
        }
        kitDeviceList {
          id
          device {
            id
            referenceId
            macAddress
            type
          }
        }
        isActivated
        operatorId
        maximumEcgDevices
        maximumWeighingScaleDevices
        maximumGlucometerDevices
        maximumBpMonitorDevices
        maximumVincenseDevices
        remark
        effectiveTill
        effectiveFrom
        referenceId
        type
        id
      }
      pagination {
        totalCount
        pageLimit
        pageNumber
      }
      error {
        statusCode
        message
      }
    }
  }
`;

const initialState: TYPES.KitListState = {
  result: null,
  error: null,
  loading: false
};

export const getKitListRequest = createAsyncThunk<
  TYPES.KitList,
  {
    filter?: TYPES.KitFilterInput;
    pagination?: TYPES.PaginationInput;
    sort?: TYPES.SortInput[];
  },
  {extra: ApolloClient<NormalizedCacheObject>;}>(

  "kit/get_kit_list",
  async (
  params: {
    filter?: TYPES.KitFilterInput;
    pagination?: TYPES.PaginationInput;
    sort?: TYPES.SortInput[];
  },
  { extra: client }) =>
  {
    try {
      const response = await client.query({
        query: GRAPHQL,
        variables: params
      });
      return response.data.getKitList;
    } catch (error) {
      const err = error as {message?: String;statusCode?: String;};
      return Promise.reject({
        message: err.message || "An error occurred",
        statusCode: err.statusCode || "500"
      });
    }
  }
);

const getKitListSlice = createSlice({
  name: "get_kit_list",
  initialState: initialState,
  reducers: {
    getKitListClear: (state) => {
      state.result = null;
      state.error = null;
      state.loading = false;
    }
  },
  extraReducers: (builder) => {
    builder.
    addCase(getKitListRequest.pending, (state) => {
      state.result = null;
      state.error = null;
      state.loading = true;
    }).
    addCase(getKitListRequest.fulfilled, (state, action) => {
      state.result = action.payload;
      state.loading = false;
    }).
    addCase(getKitListRequest.rejected, (state, action) => {
      state.error = action.payload as Error || {
        message: "Failed to fetch",
        statusCode: "500"
      };
      state.loading = false;
    });
  }
});

export const { getKitListClear } = getKitListSlice.actions;

export const getKitList = createSelector(
  (state: RootState) => state.kit.getKitList.result,
  (state: RootState) => state.kit.getKitList.error,
  (state: RootState) => state.kit.getKitList.loading,
  (result, error, loading) => ({
    result,
    error,
    loading
  })
);

export default getKitListSlice.reducer;